import { DateTime } from '@ksyos/date-time';
import type {
    VektisAdres,
    VektisCommunicatieGegevens,
    VektisErkenning,
    VektisOrganisatie,
    VektisOrganisatieResponse,
} from '@common/interfaces/api/vektisApi';
import type { IOrganization } from '@frontend/components/interfaces';
import type { AbortableFetch } from '@frontend/store/fetch';
import { convertUpperCasetoStartCase } from '@frontend/utils/convertUpperCaseToStartCase';

const visitorAddressCode = '1';
const emailAddressCode = '1';
const websiteCode = '16';
const phoneNumberCode = '2';
const kvkNumberCode = '100101';

export type VektisOrganizationSettings = Pick<
    IOrganization,
    | 'agbCodes'
    | 'name'
    | 'nameStatutory'
    | 'kvkNumber'
    | 'phoneNumber'
    | 'emailAddressRequestInbox'
    | 'url'
    | 'street'
    | 'houseNumber'
    | 'houseNumberSuffix'
    | 'zip'
    | 'city'
    | 'state'
>;

export async function getVektisOrganization(agbCode: string | undefined, abortableFetch: AbortableFetch) {
    if (!agbCode) {
        return;
    }

    const url = `/api/vektis/organizations/${agbCode}`;

    return await abortableFetch.fetchJson<VektisOrganisatieResponse>(url, 'GET', undefined, undefined, [404, 400]);
}

export function isVektisOrganizationInactive(organization: VektisOrganisatie | undefined): boolean {
    return Boolean(organization?.einde && DateTime.now().isAfter(organization.einde));
}

export function mapVektisResponseToOrganization(response: VektisOrganisatieResponse | undefined) {
    if (!response?.organization) {
        return;
    }
    const { organization } = response;
    const visitorAddress = getVisitorAddress(organization.adressen);
    const mappedOrganization: Partial<VektisOrganizationSettings> = {
        agbCodes: [{ agbCode: organization.agbCode, endDate: null, startDate: DateTime.today().format('YYYY-MM-DD') }],
        city: convertUpperCasetoStartCase(visitorAddress?.plaats),
        emailAddressRequestInbox: getActiveEmailAddress(organization.communicatiegegevens),
        houseNumber: visitorAddress?.huisnummer?.toString(),
        houseNumberSuffix: visitorAddress?.huisnummerToevoeging,
        kvkNumber: getActiveKvkNumber(organization.erkenningen),
        name: convertUpperCasetoStartCase(organization.naam),
        nameStatutory: organization.statutaireNaam ?? organization.handelsnaam,
        phoneNumber: getActivePhoneNumber(organization.communicatiegegevens),
        state: visitorAddress?.provincie?.omschrijving,
        street: visitorAddress?.straat,
        url: getActiveWebsite(organization.communicatiegegevens),
        zip: mapPostcode(visitorAddress?.postcode),
    };

    return mappedOrganization;
}

function getVisitorAddress(addresses: VektisAdres[]): VektisAdres | undefined {
    if (!addresses?.length) {
        return;
    }

    return (
        addresses.find(
            (address: VektisAdres) =>
                address.type.code === visitorAddressCode && !address.einde && new Date(address.aanvang) <= new Date()
        ) || addresses[0]
    );
}

function getActiveCommunicationDetail(
    communicationDetails: VektisCommunicatieGegevens[],
    typeCode: string
): string | undefined {
    const activeDetail = communicationDetails?.find(
        (comm: VektisCommunicatieGegevens) =>
            comm.type.code === typeCode && !comm.einde && new Date(comm.aanvang) <= new Date()
    );

    return activeDetail ? activeDetail.gegeven : undefined;
}

function getActivePhoneNumber(communicationDetails: VektisCommunicatieGegevens[]): string | undefined {
    return getActiveCommunicationDetail(communicationDetails, phoneNumberCode);
}

function getActiveEmailAddress(communicationDetails: VektisCommunicatieGegevens[]): string | undefined {
    return getActiveCommunicationDetail(communicationDetails, emailAddressCode);
}

function getActiveWebsite(communicationDetails: VektisCommunicatieGegevens[]): string | undefined {
    return getActiveCommunicationDetail(communicationDetails, websiteCode);
}

function mapPostcode(postcode?: string): string | undefined {
    if (!postcode) {
        return;
    }

    const trimmedPostcode = postcode.trim();

    if (trimmedPostcode.length === 6) {
        return `${trimmedPostcode.slice(0, 4)} ${trimmedPostcode.slice(4).toUpperCase()}`;
    } else {
        return trimmedPostcode.toUpperCase();
    }
}

function getActiveKvkNumber(erkenningen: VektisErkenning[]): string | undefined {
    const kvkErkenning = erkenningen?.find(
        (erkenning) => erkenning.code === kvkNumberCode && !erkenning.einde && new Date(erkenning.aanvang) <= new Date()
    );

    return kvkErkenning ? kvkErkenning.registratieNummer : undefined;
}
